import { useEffect, useState, useCallback } from 'react';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';
import { ThumbsUp, Flag, Reply, Trash2, MessageSquare, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleComments({ slug }) {
  const { isAuthenticated, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const auth = useCallback(async () => {
    if (!isAuthenticated) return { 'Content-Type': 'application/json' };
    const t = await getAccessTokenSilently();
    return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
  }, [isAuthenticated, getAccessTokenSilently]);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/public/articles/${slug}/comments?page=${p}&limit=20`);
      const d = await r.json();
      if (p === 1) setComments(d.comments || []);
      else setComments(prev => [...prev, ...(d.comments || [])]);
      setTotal(d.total || 0);
    } catch {}
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(1); setPage(1); }, [load]);

  async function submit(text, parent_id) {
    if (!isAuthenticated) { loginWithRedirect(); return; }
    if (!text.trim()) return;
    try {
      const h = await auth();
      const r = await fetch(`${API}/public/articles/${slug}/comments`, {
        method: 'POST', headers: h, body: JSON.stringify({ body: text.trim(), parent_id: parent_id || null }),
      });
      if (!r.ok) throw new Error();
      toast.success('Komentar objavljen');
      setBody(''); setReplyBody(''); setReplyTo(null);
      load(1);
    } catch { toast.error('Greška pri objavi'); }
  }

  async function like(cid) {
    if (!isAuthenticated) { loginWithRedirect(); return; }
    const h = await auth();
    const r = await fetch(`${API}/public/comments/${cid}/like`, { method: 'POST', headers: h });
    if (r.ok) load(1);
  }

  async function flag(cid) {
    if (!isAuthenticated) { loginWithRedirect(); return; }
    if (!window.confirm('Prijaviti ovaj komentar?')) return;
    const h = await auth();
    await fetch(`${API}/public/comments/${cid}/flag`, { method: 'POST', headers: h });
    toast.success('Prijavljeno');
    load(1);
  }

  async function del(cid) {
    if (!window.confirm('Obrisati komentar?')) return;
    const h = await auth();
    const r = await fetch(`${API}/public/comments/${cid}`, { method: 'DELETE', headers: h });
    if (r.ok) load(1);
  }

  const mySub = user?.sub;

  return (
    <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800" data-testid="article-comments">
      <h2 className="text-base font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 border-l-4 border-red-600 pl-3 mb-6 flex items-center gap-2">
        <MessageSquare size={16} /> Komentari ({total})
      </h2>

      {/* Input */}
      {isAuthenticated ? (
        <form onSubmit={(e) => { e.preventDefault(); submit(body, null); }} className="mb-6" data-testid="comment-form">
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Napišite komentar…"
            maxLength={2000}
            className="w-full bg-gray-50 dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-red-400"
            data-testid="comment-input" />
          <div className="flex justify-end mt-2">
            <button type="submit" disabled={!body.trim()} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50" data-testid="comment-submit">
              Objavi
            </button>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-4 border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-[#0b1220] text-sm text-gray-600 dark:text-gray-300 flex items-center justify-between">
          <span>Prijavite se za komentiranje.</span>
          <button onClick={() => loginWithRedirect()} className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs font-semibold" data-testid="comment-login-btn">Prijava</button>
        </div>
      )}

      {loading && comments.length === 0 && <p className="text-xs text-gray-400">Učitavam komentare…</p>}

      <div className="space-y-5">
        {comments.map(c => (
          <div key={c.id} className="" data-testid={`comment-${c.id}`}>
            <CommentCard c={c} onLike={like} onFlag={flag} onReply={() => setReplyTo(c.id)} onDelete={del} isOwner={mySub && c.user_id === mySub} />
            {/* Replies */}
            {(c.replies || []).map(r => (
              <div key={r.id} className="pl-10 mt-3">
                <CommentCard c={r} onLike={like} onFlag={flag} onReply={() => {}} onDelete={del} isOwner={mySub && r.user_id === mySub} isReply />
              </div>
            ))}
            {replyTo === c.id && isAuthenticated && (
              <form onSubmit={(e) => { e.preventDefault(); submit(replyBody, c.id); }} className="pl-10 mt-3" data-testid={`comment-reply-form-${c.id}`}>
                <textarea value={replyBody} onChange={e => setReplyBody(e.target.value)} rows={2}
                  placeholder="Odgovor…"
                  className="w-full bg-gray-50 dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-red-400" />
                <div className="flex gap-2 mt-1 justify-end">
                  <button type="button" onClick={() => setReplyTo(null)} className="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 px-2">Odustani</button>
                  <button type="submit" disabled={!replyBody.trim()} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-md disabled:opacity-50" data-testid={`comment-reply-submit-${c.id}`}>Odgovori</button>
                </div>
              </form>
            )}
          </div>
        ))}
      </div>

      {comments.length < total && (
        <div className="mt-6 text-center">
          <button onClick={() => { const np = page + 1; setPage(np); load(np); }} className="px-4 py-2 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200" data-testid="comments-load-more">
            Učitaj više
          </button>
        </div>
      )}
    </section>
  );
}

function CommentCard({ c, onLike, onFlag, onReply, onDelete, isOwner, isReply }) {
  const date = c.created_at ? new Date(c.created_at).toLocaleString('hr-HR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className="flex gap-3">
      {c.user_picture ? (
        <img src={c.user_picture} alt="" className="w-8 h-8 rounded-full" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500"><UserIcon size={14} /></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{c.user_name}</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{date}</span>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5 whitespace-pre-wrap break-words">{c.body}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <button onClick={() => onLike(c.id)} className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-red-600 flex items-center gap-1" data-testid={`comment-like-${c.id}`}>
            <ThumbsUp size={11} /> {c.likes || 0}
          </button>
          {!isReply && (
            <button onClick={onReply} className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-red-600 flex items-center gap-1" data-testid={`comment-reply-${c.id}`}>
              <Reply size={11} /> Odgovori
            </button>
          )}
          <button onClick={() => onFlag(c.id)} className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-amber-600 flex items-center gap-1" data-testid={`comment-flag-${c.id}`}>
            <Flag size={11} /> Prijavi
          </button>
          {isOwner && (
            <button onClick={() => onDelete(c.id)} className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-red-600 flex items-center gap-1" data-testid={`comment-delete-${c.id}`}>
              <Trash2 size={11} /> Obriši
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
